import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { authSelector } from "../../store/selectors/index";
import { Modal, message, Select } from "antd";

import {
  AiFillSave,
  AiFillDelete,
  AiFillFileAdd,
  AiFillEdit,
} from "react-icons/ai";
import { LuView } from "react-icons/lu";
import { BiExit } from "react-icons/bi";

import {
  getLatestItemCode,
  createItem,
  getAllItemForUpdate,
  updateSingleItem,
  deleteSingleItem,
} from "../../api/endpoints/item.js";
import Header from "../header/header";
import "../saleData/saleData.css";

const { Option } = Select;

const Item = () => {
  const { user, access_token } = useSelector(authSelector);

  const [items, setItems] = useState([]);
  const [fieldsShow, setFieldsShow] = useState(false);
  const [type, setType] = useState("ADD");
  const [loading, setLoading] = useState(false);

  const [state, setState] = useState({
    id: "",
    itemCode: "",
    itemName: "",
    sRate: "",
    rateChange: 0,
  });

  const [modal, contextHolder] = Modal.useModal();

  // =========================================================
  // CLEAR FORM
  // =========================================================
  const clearState = () => {
    setState({
      id: "",
      itemCode: "",
      itemName: "",
      sRate: "",
      rateChange: 0,
    });

    setFieldsShow(false);
    setType("ADD");
  };

  // =========================================================
  // INPUT CHANGE
  // =========================================================
  const onChangeText = (e) => {
    const { name, value } = e.target;

    setState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =========================================================
  // GET ALL ITEMS
  // =========================================================
  const getAllItems = async () => {
    try {
      setLoading(true);

      const results = await getAllItemForUpdate(access_token);

      if (results?.success === false) {
        message.error(results.message || "Failed to load items");
        return;
      }

      const data = Array.isArray(results)
        ? results
        : results?.data || [];

      setItems(data);
    } catch (error) {
      console.error("Get items error:", error);
      message.error("Failed to load items");
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // INITIAL LOAD
  // =========================================================
  useEffect(() => {
    getAllItems();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================================================
  // ADD ITEM
  // =========================================================
  const addItem = async () => {
    try {
      setLoading(true);

      const result = await getLatestItemCode(access_token);

      if (!result?.data) {
        message.error("Unable to generate Item Code");
        return;
      }

      setState({
        id: "",
        itemCode: Number(result.data),
        itemName: "",
        sRate: "",
        rateChange: 0,
      });

      setType("SAVE");
      setFieldsShow(true);
    } catch (error) {
      console.error("Get latest item code error:", error);
      message.error("Unable to generate Item Code");
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // VALIDATE FORM
  // =========================================================
  const validateForm = () => {
    if (!state.itemCode) {
      message.error("Please enter Item Code");
      return false;
    }

    if (!state.itemName || !state.itemName.trim()) {
      message.error("Please enter Item Name");
      return false;
    }

    if (
      state.sRate === "" ||
      state.sRate === null ||
      state.sRate === undefined
    ) {
      message.error("Please enter Sale Rate");
      return false;
    }

    return true;
  };

  // =========================================================
  // SAVE NEW ITEM
  // =========================================================
  const saveItem = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        itemCode: Number(state.itemCode),
        itemName: state.itemName.trim(),
        sRate: Number(state.sRate),
        rateChange: Number(state.rateChange),
        siteId: Number(user.siteId),
      };

      console.log("CREATE ITEM:", payload);

      const results = await createItem(payload, access_token);

      if (results?.success) {
        message.success(results.message || "Item successfully created!");

        await getAllItems();

        clearState();
      } else {
        message.error(results?.message || "Failed to create item");
      }
    } catch (error) {
      console.error("Create item error:", error);

      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to create item"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // OPEN EDIT MODE
  // =========================================================
  const editItem = async () => {
    setType("EDIT");
    setFieldsShow(true);

    await getAllItems();
  };

  // =========================================================
  // SELECT ITEM FOR EDIT
  // =========================================================
  const handleItemSelect = (itemCode) => {
    const selectedItem = items.find(
      (item) => Number(item.itemCode) === Number(itemCode)
    );

    if (!selectedItem) {
      message.error("Item not found");
      return;
    }

    setState({
      id: selectedItem.id || "",
      itemCode: selectedItem.itemCode || "",
      itemName: selectedItem.itemName || "",
      sRate: selectedItem.sRate ?? "",
      // rateChange: Number(selectedItem.rateChange) || 0,
    });
  };

  // =========================================================
  // UPDATE ITEM
  // =========================================================
  const updateItem = async () => {
    if (!validateForm()) return;

    if (!state.id) {
      message.error("Please select an Item");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        id: state.id,
        itemCode: Number(state.itemCode),
        itemName: state.itemName.trim(),
        sRate: Number(state.sRate),
        rateChange: 1,
      };

      console.log("UPDATE ITEM:", payload);

      const results = await updateSingleItem(payload, access_token);

      if (results?.success) {
        message.success(results.message || "Item successfully updated!");

        await getAllItems();

        clearState();
      } else {
        message.error(results?.message || "Failed to update item");
      }
    } catch (error) {
      console.error("Update item error:", error);

      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update item"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // DELETE CONFIRMATION
  // =========================================================
  const confirmDelete = () => {
    if (!state.itemCode) {
      message.error("Please select an Item");
      return;
    }

    modal.confirm({
      title: "Delete Item",
      content: `Are you sure you want to delete "${state.itemName}"?`,
      okText: "Delete",
      cancelText: "Cancel",
      okButtonProps: {
        danger: true,
      },
      onOk: deleteItem,
    });
  };

  // =========================================================
  // DELETE ITEM
  // =========================================================
  const deleteItem = async () => {
    try {
      setLoading(true);

      const payload = {
        itemCode: Number(state.itemCode),
        siteId: Number(user.siteId),
      };

      console.log("DELETE ITEM:", payload);

      const results = await deleteSingleItem(payload, access_token);

      if (results?.success) {
        message.success(results.message || "Item deleted successfully!");

        await getAllItems();

        clearState();
      } else {
        message.error(results?.message || "Failed to delete item");
      }
    } catch (error) {
      console.error("Delete item error:", error);

      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to delete item"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // OPEN DELETE MODE
  // =========================================================
  const deleteMode = () => {
    setType("DELETE");
    setFieldsShow(true);

    getAllItems();
  };

  // =========================================================
  // VIEW ITEMS
  // =========================================================
  const viewItems = async () => {
    try {
      setLoading(true);

      await getAllItems();

      setType("VIEW");
      setFieldsShow(false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // DONE BUTTON
  // =========================================================
  const handleDone = () => {
    if (type === "EDIT") {
      updateItem();
    } else if (type === "DELETE") {
      confirmDelete();
    }
  };

  // =========================================================
  // EXIT
  // =========================================================
  const handleExit = () => {
    clearState();
    setType("ADD");
  };

  // =========================================================
  // FORMAT RATE
  // =========================================================
  const formatRate = (rate) => {
    if (rate === null || rate === undefined || rate === "") {
      return "0.00";
    }

    return Number(rate).toFixed(2);
  };

  // =========================================================
  // RENDER
  // =========================================================
  return (
    <>
      {contextHolder}

      <Header />

      <div className="min-h-screen background text-black pb-8">
        {/* =====================================================
            HEADER
        ===================================================== */}
        <div className="flex justify-center pt-6 px-4">
          <div className="w-full max-w-6xl">
            <div className="bg-cyan-600 rounded-t-xl shadow-xl px-5 py-4 flex items-center justify-between text-white">
              <h1 className="text-xl md:text-2xl font-bold">
                ITEM MANAGEMENT
              </h1>

              <span className="text-sm font-semibold">
                Site: {user?.siteId}
              </span>
            </div>

            {/* =================================================
                FORM
            ================================================= */}
            {(fieldsShow || type === "VIEW") && type !== "VIEW" && (
              <div className="bg-white bg-opacity-70 backdrop-filter backdrop-blur-sm border-x border-white px-4 md:px-8 py-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* ITEM CODE */}
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Item Code
                    </label>

                    {type === "EDIT" || type === "DELETE" ? (
                      <Select
                        showSearch
                        className="w-full"
                        placeholder="Select Item"
                        optionFilterProp="children"
                        value={
                          state.itemCode === ""
                            ? undefined
                            : state.itemCode
                        }
                        onChange={handleItemSelect}
                        filterOption={(input, option) =>
                          String(option?.children || "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {items.map((item) => (
                          <Option
                            key={item.id || item.itemCode}
                            value={item.itemCode}
                          >
                            {item.itemCode} - {item.itemName}
                          </Option>
                        ))}
                      </Select>
                    ) : (
                      <input
                        readOnly
                        type="number"
                        name="itemCode"
                        value={state.itemCode}
                        className="w-full h-10 rounded-md bg-white border border-cyan-600 text-black px-3 outline-none"
                      />
                    )}
                  </div>

                  {/* ITEM NAME */}
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Item Name
                    </label>

                    <input
                      type="text"
                      name="itemName"
                      value={state.itemName}
                      onChange={onChangeText}
                      disabled={type === "DELETE"}
                      placeholder="Enter Item Name"
                      className="w-full h-10 rounded-md bg-white border border-cyan-600 text-black px-3 outline-none focus:border-cyan-700 disabled:opacity-60"
                    />
                  </div>

                  {/* SALE RATE */}
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Sale Rate
                    </label>

                    <input
                      type="number"
                      step="0.01"
                      name="sRate"
                      value={state.sRate}
                      onChange={onChangeText}
                      disabled={type === "DELETE"}
                      placeholder="Enter Sale Rate"
                      className="w-full h-10 rounded-md bg-white border border-cyan-600 text-black px-3 outline-none focus:border-cyan-700 disabled:opacity-60"
                    />
                  </div>

                  {/* RATE CHANGE */}
                 
                </div>

                {/* SITE INFORMATION */}
                <div className="mt-5 bg-white bg-opacity-60 border border-cyan-100 rounded-lg px-4 py-3 shadow-sm">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-400">Site ID:</span>{" "}
                      <span className="font-bold">{user?.siteId}</span>
                    </div>

                    <div>
                      <span className="text-gray-400">Item ID:</span>{" "}
                      <span className="font-bold">
                        {state.id || "New Item"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* =================================================
                VIEW TABLE
            ================================================= */}
            {type === "VIEW" && (
              <div className="bg-white bg-opacity-70 backdrop-filter backdrop-blur-sm border-x border-white p-4">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg md:text-xl font-bold">
                    ALL ITEMS
                  </h2>

                    <span className="bg-cyan-600 text-white px-3 py-1 rounded-md text-sm font-semibold">
                    {items.length} Items
                  </span>
                </div>

                <div className="overflow-x-auto rounded-lg border border-cyan-200 shadow-sm">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-cyan-600 text-white">
                        <th className="px-4 py-3 text-left">
                          S#
                        </th>

                        <th className="px-4 py-3 text-left">
                          Item Code
                        </th>

                        <th className="px-4 py-3 text-left">
                          Item Name
                        </th>

                        <th className="px-4 py-3 text-right">
                          Sale Rate
                        </th>

                        <th className="px-4 py-3 text-center">
                          Rate Change
                        </th>

                        <th className="px-4 py-3 text-center">
                          Site ID
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {items.length > 0 ? (
                        items.map((item, index) => (
                          <tr
                            key={item.id || index}
                            className="border-b border-cyan-100 hover:bg-cyan-50 transition"
                          >
                            <td className="px-4 py-3">
                              {index + 1}
                            </td>

                            <td className="px-4 py-3 font-semibold">
                              {item.itemCode}
                            </td>

                            <td className="px-4 py-3">
                              {item.itemName}
                            </td>

                            <td className="px-4 py-3 text-right font-semibold">
                              Rs. {formatRate(item.sRate)}
                            </td>

                            <td className="px-4 py-3 text-center">
                              {Number(item.rateChange) === 1 ? (
                                <span className="bg-green-600 px-3 py-1 rounded-full text-xs font-bold">
                                  YES
                                </span>
                              ) : (
                                <span className="bg-gray-600 px-3 py-1 rounded-full text-xs font-bold">
                                  NO
                                </span>
                              )}
                            </td>

                            <td className="px-4 py-3 text-center">
                              {item.siteId}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="6"
                            className="px-4 py-8 text-center text-gray-400"
                          >
                            No items found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* =================================================
                BUTTONS
            ================================================= */}
            <div className="bg-white bg-opacity-70 backdrop-filter backdrop-blur-sm border border-white rounded-b-xl px-4 py-5 shadow-xl">
              <div className="flex flex-wrap justify-center gap-2">
                {/* ADD */}
                {/* {type === "ADD" && (
                  <button
                    onClick={addItem}
                    disabled={loading}
                    className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 flex items-center text-white py-2 px-5 rounded-md font-semibold transition"
                  >
                    <AiFillFileAdd className="mr-2" />
                    Add
                  </button>
                )} */}

                {/* SAVE */}
                {type === "SAVE" && (
                  <button
                    onClick={saveItem}
                    disabled={loading}
                    className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 flex items-center text-white py-2 px-5 rounded-md font-semibold transition"
                  >
                    <AiFillSave className="mr-2" />
                    {loading ? "Saving..." : "Save"}
                  </button>
                )}

                {/* VIEW */}
                {type === "ADD" && (
                  <button
                    onClick={viewItems}
                    disabled={loading}
                    className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 flex items-center text-white py-2 px-5 rounded-md font-semibold transition"
                  >
                    <LuView className="mr-2" />
                    View
                  </button>
                )}

                {/* EDIT */}
                {type === "ADD" && (
                  <button
                    onClick={editItem}
                    disabled={loading}
                    className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 flex items-center text-white py-2 px-5 rounded-md font-semibold transition"
                  >
                    <AiFillEdit className="mr-2" />
                    Update
                  </button>
                )}

                {/* DELETE */}
                {/* {type === "ADD" && (
                  <button
                    onClick={deleteMode}
                    disabled={loading}
                    className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 flex items-center text-white py-2 px-5 rounded-md font-semibold transition"
                  >
                    <AiFillDelete className="mr-2" />
                    Delete
                  </button>
                )} */}

                {/* DONE */}
                {(type === "EDIT" || type === "DELETE") && (
                  <button
                    onClick={handleDone}
                    disabled={loading}
                    className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 flex items-center text-white py-2 px-6 rounded-md font-semibold transition"
                  >
                    {loading ? "Processing..." : "DONE"}
                  </button>
                )}

                {/* EXIT */}
                {type !== "ADD" && (
                  <button
                    onClick={handleExit}
                    disabled={loading}
                    className="bg-gray-600 hover:bg-gray-700 disabled:opacity-50 flex items-center text-white py-2 px-5 rounded-md font-semibold transition"
                  >
                    <BiExit className="mr-2" />
                    Exit
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Item;